import classNames from "classnames";
import Clickable from "components/Clickable";
import inputStyles from "components/Input/input.module.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import {
    ComboBox,
    Input,
    ListBox,
    ListBoxItem,
    Popover,
} from "react-aria-components";

import CloseIcon from "../../public/svg/x.svg";

import styles from "./filtered-dropdown.module.scss";

/**
 * Delivers the key/value pair that the backend requires.
 * `number | null` is a bit hacky; its purpose is to send a signal that the other
 * number needs deleted.
 */
export type AttributeKeyValue = readonly [number | null, number];

export type AttributeOptionChoice = {
    value: AttributeKeyValue;
    label: string;
};

export type Props = {
    choices: AttributeOptionChoice[];
    defaultValue: AttributeOptionChoice | null;
    placeholder?: string;
    onChange?: (value: AttributeKeyValue) => void;
} & Omit<
    React.ComponentPropsWithoutRef<typeof ComboBox>,
    "children" | "defaultValue" | "onChange"
>;

export const FilteredDropdown = ({
    choices,
    defaultValue,
    placeholder,
    onChange,
    ...rest
}: Props) => {
    const [term, setTerm] = useState(defaultValue?.label || "");
    useEffect(() => setTerm(defaultValue?.label || ""), [defaultValue]);
    const inputRef = useRef<HTMLInputElement>(null);

    const choicesMap = useMemo(() => {
        let id = 0;
        const map = new Map<number, AttributeOptionChoice>();
        choices.forEach((choice) => {
            map.set(id++, choice);
        });
        return map;
    }, [choices]);

    const clearSelection = () => {
        if (defaultValue) {
            onChange?.([null, defaultValue.value[1]]);
        } else {
            setTerm("");
        }
        inputRef.current?.focus();
    };

    return (
        <ComboBox
            {...rest}
            className={styles.comboBoxWrapper}
            menuTrigger="focus"
            inputValue={term}
            onInputChange={(newValue) => setTerm(newValue)}
            defaultItems={choices}
            onSelectionChange={(value) => {
                onChange?.(choicesMap.get(value as number)?.value ?? [null, 0]);
            }}
        >
            <div className={styles.comboBoxInputWrapper}>
                <Input
                    className={inputStyles.textlikeInput}
                    placeholder={placeholder}
                    ref={inputRef}
                />
                {(!!defaultValue || term !== "") && (
                    <Clickable
                        type="button"
                        unstyled={true}
                        className={styles.clearIcon}
                        onClick={() => clearSelection()}
                    >
                        <CloseIcon role="img" aria-label="" />
                        <VisuallyHidden>Clear selection</VisuallyHidden>
                    </Clickable>
                )}
            </div>
            <Popover
                offset={0}
                shouldFlip={false}
                className={classNames({
                    [styles.popover]: true,
                    [styles.forceShow]: term === "",
                })}
            >
                <ListBox
                    className={styles.list}
                    renderEmptyState={() => (
                        <span className={styles.empty}> No results found </span>
                    )}
                >
                    {Array.from(choicesMap.entries()).map(([id, result]) => (
                        <MatchedItem
                            className={styles.option}
                            key={id}
                            id={id}
                            term={term}
                            label={result.label}
                        />
                    ))}
                </ListBox>
            </Popover>
        </ComboBox>
    );
};

function MatchedItem({
    term,
    label,
    ...props
}: React.ComponentProps<typeof ListBoxItem> & { term: string; label: string }) {
    const matchIndex = label.toLowerCase().search(term.toLowerCase());
    const children = [];
    if (matchIndex > -1) {
        if (matchIndex > 0) {
            children.push(
                <span data-selected-value="true" key="start">
                    {label.substring(0, matchIndex)}
                </span>,
            );
        }
        children.push(
            <span data-user-value="true" key="middle">
                {label.substring(matchIndex, term.length)}
            </span>,
        );
        if (matchIndex + term.length < label.length) {
            children.push(
                <span data-suggested-value="true" key="end">
                    {label.substring(matchIndex + term.length)}
                </span>,
            );
        }
    } else {
        children.push(<span key="all">{label}</span>);
    }
    return (
        <ListBoxItem {...props} textValue={label}>
            {children}
        </ListBoxItem>
    );
}
